import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import useAxiosAuth from '../hooks/useAxiosAuth';
import baseURL from '../utils/baseURL';

// APPOINTMENT SHARING, doctor side (doc/one-patient-row.md §9, Phase 4).
//
// A patient shares ONE past visit — at any hospital — with a doctor they have
// been treated by. This slice is the receiving half: the doctor's list of what
// has been shared with them, and the read-only snapshot of one such visit.
//
// THE SNAPSHOT IS THE PLATFORM'S ONLY CROSS-TENANT CLINICAL READ, and the
// share row is the whole authorisation — re-checked on every request. So a
// 403 here does not mean "you are not a doctor", it means the patient has
// taken the permission back since the list was drawn, and the page says so in
// those words rather than showing a failure. The `code` is kept for that.

const serializeError = (error) => {
    const data = error?.response?.data;
    if (data && typeof data === 'object') {
        return {
            code: data.code || null,
            message: data.message || null,
            status: error.response?.status || null,
        };
    }
    return { code: null, message: error?.message || 'Request failed', status: null };
};

// GET /appointment-shares/received
// → { shares:[{ shareId, appointmentId, patientId, patientName, appointmentDate,
//               doctorName, hospitalName, grantedAt }] }
export const fetchReceivedShares = createAsyncThunk(
    'appointmentShare/fetchReceived',
    async (_, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.get(`${baseURL}/appointment-shares/received`);
            return res.data?.shares || [];
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

// GET /appointment-shares/received/:shareId
// → { appointment:{date,reason,status}, visitNotes:{written,consultation,summary},
//     prescription:{items[]}, doctor:{name,specialty}, hospital:{name,city},
//     patient:{patientId,name} }
//
// The recording is deliberately NOT in there: consenting to show one
// consultation is not consenting to hand over a recording of it.
export const fetchReceivedShare = createAsyncThunk(
    'appointmentShare/fetchReceivedOne',
    async ({ shareId }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.get(`${baseURL}/appointment-shares/received/${shareId}`);
            return { shareId, snapshot: res.data };
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

// ---------------------------------------------------------------------------
// ASKING (visit sharing v2, owner 24 Sep 2026). The doctor can also ASK a
// patient they have treated to share previous visits; the patient grants
// (choosing which) or declines in their own app. The doctor never picks the
// visits — the patient does — so this side only sends the ask and shows its
// state.

// POST /appointment-shares/requests { patientId, note? } → 201 { request }
// 403 NOT_A_LINKED_PATIENT, 409 REQUEST_PENDING (one open ask per pair).
export const sendShareRequest = createAsyncThunk(
    'appointmentShare/sendRequest',
    async ({ patientId, note }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const body = { patientId };
            const trimmed = String(note || '').trim();
            if (trimmed) body.note = trimmed.slice(0, 300);
            const res = await axiosInstance.post(`${baseURL}/appointment-shares/requests`, body);
            return res.data?.request || null;
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

// GET /appointment-shares/requests/sent → { requests:[request…] } (90 days)
export const fetchSentShareRequests = createAsyncThunk(
    'appointmentShare/fetchSentRequests',
    async (_, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.get(`${baseURL}/appointment-shares/requests/sent`);
            return res.data?.requests || [];
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

/**
 * The newest ask this doctor sent about one person, or null. Newest wins: an
 * ask that expired last month must not hide the one sent this morning.
 */
export const latestRequestFor = (requests, patientId) => {
    if (!patientId || !Array.isArray(requests)) return null;
    return requests
        .filter((r) => r && r.patientId === patientId)
        .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')))[0] || null;
};

// ---------------------------------------------------------------------------
// UNSEEN SHARES — the sidebar badge. A share that arrives over the socket
// while the doctor is elsewhere is counted until they open "Shared with me".
// sessionStorage, not localStorage: the count belongs to this sitting — the
// page itself lists everything on the next login, and a count surviving a
// logout would greet the NEXT person on a shared desk computer.
const UNSEEN_KEY = 'moc.sharedVisits.unseen';

const readUnseen = () => {
    try {
        const n = parseInt(sessionStorage.getItem(UNSEEN_KEY) || '0', 10);
        return Number.isFinite(n) && n > 0 ? n : 0;
    } catch {
        return 0;
    }
};
const writeUnseen = (n) => {
    try {
        if (n > 0) sessionStorage.setItem(UNSEEN_KEY, String(n));
        else sessionStorage.removeItem(UNSEEN_KEY);
    } catch { /* storage blocked — the badge just lives in memory */ }
};

// Thunks rather than reducers so the storage write stays out of the reducer.
export const noteUnseenShare = () => (dispatch, getState) => {
    const next = (getState().appointmentShare?.unseenCount || 0) + 1;
    writeUnseen(next);
    dispatch(appointmentShareSlice.actions.setUnseenCount(next));
};
export const clearUnseenShares = () => (dispatch) => {
    writeUnseen(0);
    dispatch(appointmentShareSlice.actions.setUnseenCount(0));
};

const appointmentShareSlice = createSlice({
    name: 'appointmentShare',
    initialState: {
        received: [],
        receivedLoading: false,
        receivedError: null,
        // The one snapshot currently open, and the share it belongs to.
        snapshot: null,
        snapshotShareId: null,
        snapshotLoading: false,
        snapshotError: null,
        // Asks this doctor has sent (last 90 days), for the status chip.
        sentRequests: [],
        sentLoaded: false,
        sending: false,
        unseenCount: readUnseen(),
    },
    reducers: {
        setUnseenCount(state, action) {
            state.unseenCount = Math.max(0, Number(action.payload) || 0);
        },
        // visit-share:received — a patient just shared a visit. Prepended so
        // it is the first row; deduped because a grant of several visits and
        // a later list refresh can both carry the same share.
        shareReceived(state, action) {
            const share = action.payload;
            if (!share || !share.shareId) return;
            state.received = [share, ...state.received.filter((s) => s.shareId !== share.shareId)];
        },
        // visit-share:revoked — the patient took it back. It leaves the list
        // at once so the doctor does not click a row that will only 403.
        shareRevoked(state, action) {
            const shareId = action.payload;
            state.received = state.received.filter((s) => s.shareId !== shareId);
        },
        // visit-share:decided — the patient answered an ask; the chip on
        // their record flips without a reload.
        shareRequestDecided(state, action) {
            const { requestId, status, count } = action.payload || {};
            const r = state.sentRequests.find((x) => x.requestId === requestId);
            if (!r) return;
            r.status = status;
            if (status === 'granted') r.grantedCount = Number(count) || 0;
            r.respondedAt = new Date().toISOString();
        },
        closeSharedVisit(state) {
            state.snapshot = null;
            state.snapshotShareId = null;
            state.snapshotError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(sendShareRequest.pending, (state) => { state.sending = true; })
            .addCase(sendShareRequest.fulfilled, (state, action) => {
                state.sending = false;
                const req = action.payload;
                if (req?.requestId) {
                    state.sentRequests = [req, ...state.sentRequests.filter((r) => r.requestId !== req.requestId)];
                }
            })
            .addCase(sendShareRequest.rejected, (state) => { state.sending = false; })
            .addCase(fetchSentShareRequests.fulfilled, (state, action) => {
                state.sentRequests = action.payload || [];
                state.sentLoaded = true;
            })
            .addCase(fetchSentShareRequests.rejected, (state) => {
                // A failed read leaves the button, never a wrong chip.
                state.sentLoaded = true;
            })
            .addCase(fetchReceivedShares.pending, (state) => {
                state.receivedLoading = true;
                state.receivedError = null;
            })
            .addCase(fetchReceivedShares.fulfilled, (state, action) => {
                state.receivedLoading = false;
                state.received = action.payload || [];
            })
            .addCase(fetchReceivedShares.rejected, (state, action) => {
                state.receivedLoading = false;
                state.receivedError = action.payload || null;
                state.received = [];
            })
            .addCase(fetchReceivedShare.pending, (state, action) => {
                state.snapshotLoading = true;
                state.snapshotError = null;
                state.snapshot = null;
                state.snapshotShareId = action.meta.arg?.shareId || null;
            })
            .addCase(fetchReceivedShare.fulfilled, (state, action) => {
                state.snapshotLoading = false;
                state.snapshot = action.payload?.snapshot || null;
                state.snapshotShareId = action.payload?.shareId || null;
            })
            .addCase(fetchReceivedShare.rejected, (state, action) => {
                state.snapshotLoading = false;
                state.snapshotError = action.payload || null;
                state.snapshot = null;
                // A revoked share must also leave the LIST, or the doctor
                // clicks the same dead row again.
                if (action.payload?.status === 403 && state.snapshotShareId) {
                    state.received = state.received.filter((s) => s.shareId !== state.snapshotShareId);
                }
            });
    },
});

export const { closeSharedVisit, shareReceived, shareRevoked, shareRequestDecided } = appointmentShareSlice.actions;
export default appointmentShareSlice.reducer;
